import React, { useState, useEffect } from 'react';
import { Check, Phone, Video, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardFooter } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Coaching = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const starterPkg = packages.coaching_starter || { amount: 97, name: 'Starter Consultation' };
  const monthlyPkg = packages.coaching_monthly || { amount: 197, name: 'Monthly Coaching' };

  const handleEnroll = async (packageId) => {
    setIsProcessing(true);
    setProcessingPlan(packageId);
    
    try {
      const response = await axios.post(`${API_URL}/api/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        user_email: user?.email || null
      });
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to start checkout. Please try again.');
      setIsProcessing(false);
      setProcessingPlan(null);
    }
  };

  const benefits = [
    {
      icon: Video,
      title: 'Monthly Video Sessions',
      description: 'One-on-one calls with Andrew to discuss challenges, celebrate wins, and plan ahead'
    },
    {
      icon: MessageCircle,
      title: 'Ongoing Support',
      description: 'Email access between sessions for urgent questions or quick guidance'
    },
    {
      icon: Phone,
      title: 'Personalized Strategies',
      description: 'Tailored advice based on your kids\' ages, personalities, and situations'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-black mb-4">
            <span className="bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 bg-clip-text text-transparent">
              Dad Coaching Program
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-medium">
            Get personalized guidance from Andrew Garcia, who has spent over 20 years helping fathers build stronger relationships with their children!
          </p>
        </div>

        {/* About Coach */}
        <Card className="mb-12 border-4 border-teal-300 hover:shadow-2xl transition-shadow bg-gradient-to-br from-white to-teal-50">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_dadtime-guides/artifacts/4ubkn1ym_Andrew%20Garcia%20circle.png" 
                alt="Andrew Garcia" 
                className="w-32 h-32 rounded-full border-4 border-teal-400 object-cover"
              />
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-3xl font-black text-gray-900 mb-2">Meet Your Coach: Andrew Garcia</h2>
                <p className="text-teal-600 font-bold mb-4">Youth & Family Expert • 20+ Years Experience</p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Andrew has dedicated his life to helping fathers build meaningful connections with their children. 
                  With over two decades of experience working with youth and families, he understands the unique 
                  challenges modern dads face and provides practical, actionable guidance that works in the real world.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Whether you're struggling with communication, navigating tough teen years, or just want to be more 
                  intentional about your fatherhood journey, Andrew provides the support, accountability, and expertise 
                  you need to become the dad you want to be.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {benefits.map((benefit, index) => (
            <Card key={index} className="border-4 border-teal-200 hover:border-teal-400 transition-all duration-300 hover:shadow-2xl transform hover:-rotate-1 bg-gradient-to-br from-white to-teal-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-400 to-teal-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                    <benefit.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pricing Cards */}
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-black text-center mb-8">Choose Your Coaching Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Starter Plan */}
            <Card className="border-4 border-teal-300 shadow-2xl">
              <CardHeader className="bg-gradient-to-br from-blue-500 via-teal-500 to-green-500 text-white rounded-t-lg">
                <div className="text-center py-6">
                  <h3 className="text-3xl font-black mb-2">{starterPkg.name || 'Starter Consultation'}</h3>
                  <p className="text-white/90 mb-4 font-medium">{starterPkg.description || 'Single session to get started'}</p>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-5xl font-black">${starterPkg.amount}</span>
                    <span className="text-xl text-white/90">one-time</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <h4 className="font-bold text-lg text-gray-900 mb-4">What's Included:</h4>
                <ul className="space-y-3 mb-8">
                  {(starterPkg.features || [
                    'One 60-minute 1-on-1 video call with Andrew',
                    'Personalized assessment of your family situation',
                    'Actionable strategies you can implement immediately',
                    'Resource recommendations tailored to your needs',
                    'Follow-up email with session notes',
                    'No ongoing commitment required'
                  ]).map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-teal-50 border-4 border-teal-300 rounded-2xl p-6 mb-6">
                  <p className="text-gray-700 text-center font-medium">
                    <span className="font-black">Perfect for:</span> Dads who want to try the program before committing to ongoing coaching.
                  </p>
                </div>
              </CardContent>
              <CardFooter className="p-8 pt-0">
                <Button 
                  size="lg" 
                  className="w-full text-lg py-6 bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 hover:from-blue-600 hover:via-teal-600 hover:to-green-600 text-white shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-200 font-black"
                  onClick={() => handleEnroll('coaching_starter')}
                  disabled={isProcessing}
                >
                  {isProcessing && processingPlan === 'coaching_starter' ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    `Book Consultation - $${starterPkg.amount}`
                  )}
                </Button>
              </CardFooter>
            </Card>

            {/* Monthly Plan - Premium */}
            <Card className="border-4 border-green-400 shadow-2xl relative">
              {monthlyPkg.badge_text && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-6 py-2 rounded-full font-black text-sm shadow-lg">
                  {monthlyPkg.badge_text}
                </div>
              )}
              <CardHeader className="bg-gradient-to-br from-green-500 via-teal-600 to-blue-600 text-white rounded-t-lg">
                <div className="text-center py-6">
                  <h3 className="text-3xl font-black mb-2">{monthlyPkg.name || 'Monthly Coaching'}</h3>
                  <p className="text-white/90 mb-4 font-medium">{monthlyPkg.description || 'Weekly calls for intensive support'}</p>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-5xl font-black">${monthlyPkg.amount}</span>
                    <span className="text-xl text-white/90">/month</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <h4 className="font-bold text-lg text-gray-900 mb-4">What's Included:</h4>
                <ul className="space-y-3 mb-8">
                  {(monthlyPkg.features || [
                    'Four 60-minute 1-on-1 video calls per month (weekly)',
                    'Priority email and text support',
                    'Personalized action plans and strategies',
                    'Access to exclusive resource library',
                    'Emergency session availability',
                    'Ongoing progress tracking and accountability'
                  ]).map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-green-50 border-4 border-green-300 rounded-2xl p-6 mb-6">
                  <p className="text-gray-700 text-center font-medium">
                    <span className="font-black">Perfect for:</span> Dads facing major challenges or who want intensive, ongoing support for rapid transformation.
                  </p>
                </div>
              </CardContent>
              <CardFooter className="p-8 pt-0">
                <Button 
                  size="lg" 
                  className="w-full text-lg py-6 bg-gradient-to-r from-green-500 via-teal-600 to-blue-600 hover:from-green-600 hover:via-teal-700 hover:to-blue-700 text-white shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-200 font-black"
                  onClick={() => handleEnroll('coaching_monthly')}
                  disabled={isProcessing}
                >
                  {isProcessing && processingPlan === 'coaching_monthly' ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    `Start Monthly Program - $${monthlyPkg.amount}/mo`
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>

        {/* Testimonial */}
        <Card className="mt-12 bg-gradient-to-br from-gray-50 to-amber-50 border-2">
          <CardContent className="p-8">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xl text-gray-700 italic mb-6">
                "Working with Andrew has been life-changing. I went from feeling disconnected and frustrated with my 
                teenagers to having real, meaningful conversations. The monthly calls give me the guidance I need, and 
                the accountability keeps me on track. Best investment I've made in my family."
              </p>
              <div>
                <p className="font-bold text-gray-900">Marcus Thompson</p>
                <p className="text-gray-600">Father of 3 • Program Member Since 2024</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                q: 'How long are the monthly sessions?',
                a: 'Each session is 60 minutes, giving us plenty of time to dive deep into your specific challenges and questions.'
              },
              {
                q: 'What if I need to reschedule a session?',
                a: 'Life happens! You can reschedule up to 24 hours before your session with no penalty.'
              },
              {
                q: 'Is there a minimum commitment?',
                a: 'We recommend at least 3 months to see real transformation, but you can cancel anytime with 30 days notice.'
              },
              {
                q: 'What if my kids are different ages?',
                a: 'Perfect! Andrew has experience with all age ranges and will tailor advice for each of your children.'
              }
            ].map((faq, index) => (
              <Card key={index} className="border-2">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{faq.q}</h3>
                  <p className="text-gray-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Coaching;
